import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import {DiagramConstants} from '../../../network-diagram/network-diagram-constants';
import {TopologyElementType} from '../../../../services/enums/topology-element-type.enum';
import {OperativeState} from '../../../../services/enums/operative-state.enum';
import {AdminStateEnum} from '../../../../services/enums/admin-state.enum';

@Injectable({
  providedIn: 'root'
})
export class ChooseTopologyDataItemToVisualsMappersService {

  nodePrefix = 'node_';
  voipPeerPrefix = 'voippeer_';
  lockedLabel = '[Locked]';
  labelOptions: any = {};

  setLabelOptions(options): void {
    this.labelOptions = _.cloneDeep(options);
  }

  getLabelOptions(): void {
    return this.labelOptions;
  }

  prepareConnectionPropertiesForDiagram(edge): void {
    if (edge.connectionType === TopologyElementType.connection) {
      edge.from = this.nodePrefix + edge.dataItem.connections[0].srcNodeId;
      edge.to = this.nodePrefix + edge.dataItem.connections[0].destNodeId;
    } else {
      edge.from = this.nodePrefix + edge.dataItem.nodeId;
      edge.to = this.voipPeerPrefix + edge.dataItem.voipPeerId;
    }

    edge.color = {
      color: this.getStatusColor(edge.dataItem, edge.connectionType, TopologyElementType.connection),
      opacity: this.getOpacity(edge.dataItem)
    };

    edge.shadow = {
      color: DiagramConstants.COLORS.black,
      size: 6
    };

    edge.label = this.isLocked(edge.dataItem, edge.connectionType, TopologyElementType.peerConnection) ?
      this.lockedLabel : ' ';   // space instead empty string (visjs bug workaround)
    edge.title = this.getTooltipContentForEdge(edge);
    edge.width = this.getWidth(edge.dataItem);
    edge.dashes = this.getDashType();

    return edge;
  }

  prepareShapePropertiesForDiagram(shape): any {
    const getLabelText = (dataItem) => {
      if (this.labelOptions.shorten === true) {
        return dataItem.name.length > this.labelOptions.length ?
          dataItem.name.substring(0, this.labelOptions.length) + '...' : dataItem.name;
      }
      return dataItem.name;
    };

    shape.color = {};
    shape.color.border = this.getBorderColor(shape.dataItem, shape.shapeType);
    shape.color.background = this.getStatusColor(shape.dataItem, shape.shapeType, TopologyElementType.node);
    shape.label = getLabelText(shape.dataItem);
    shape.title = this.getTooltipContentForShape(shape);
    shape.x = shape.dataItem.x;
    shape.y = shape.dataItem.y;

    if (shape.shapeType === TopologyElementType.node) {
      shape.image = 'assets/images/network/elements/nodes/' +
        this.getShapeType(shape.dataItem, shape.shapeType) +
        (this.isLocked(shape.dataItem, shape.shapeType, TopologyElementType.node) ? '_lock' : '') + '.svg';
    } else {
      shape.image = 'assets/images/network/elements/voippeers/' + this.getShapeType(shape.dataItem, shape.shapeType) + '.svg';
    }

    if (!this.isFiltered(shape.dataItem)) {
      shape.color.background = this.convertHex(shape.color.background, 30);
      shape.color.border = this.convertHex(shape.color.border, 30);
    }

    return shape;
  }

  getDashType(): boolean {
    return DiagramConstants.DASH_TYPES.solid;
  }

  getWidth(dataItem): number {
    if (this.isFilteredForWidth(dataItem)) {
      return DiagramConstants.CONNECTION_WIDTH_TYPES.extraThick;
    } else {
      return DiagramConstants.CONNECTION_WIDTH_TYPES.regular;
    }
  }

  getStatusColor(dataItem, shapeType, elementType): string {
    if (_.isEqual(shapeType, elementType)) {
      return DiagramConstants.COLORS.blue;
    } else {
      return DiagramConstants.COLORS.grey;
    }
  }

  isLocked(dataItem, shapeType, elementType): boolean {
    if (_.isEqual(shapeType, elementType)) {
      return _.isEqual(dataItem.adminState, AdminStateEnum.LOCKED);
    } else {
      return false;
    }
  }

  getTooltipContentForEdge(edge): string {
    let operState;
    if (edge.connectionType === TopologyElementType.connection) {
      if (edge.type === 'REGULAR') {
        operState = edge.dataItem.connections[0].operState === OperativeState.UNAVAILABLE ||
        edge.dataItem.connections[1].operState === OperativeState.UNAVAILABLE ?
          OperativeState.UNAVAILABLE : OperativeState.AVAILABLE;
        return 'Name: ' + edge.dataItem.name + '\n' +
          'State: ' + operState;
      } else {
        operState = edge.dataItem.connections[0].operState === OperativeState.UNAVAILABLE ?
          OperativeState.UNAVAILABLE : OperativeState.AVAILABLE;
        return 'Name: ' + edge.dataItem.name + '\n' +
          'State: ' + operState;
      }
    } else {
      return 'Name: ' + edge.dataItem.name + '\n' +
        'State: ' + edge.dataItem.operState;
    }
  }

  getTooltipContentForShape(shape): any {
    if (shape.shapeType === TopologyElementType.node) {
      if (shape.dataItem.productType === 'THIRD_PARTY') {
        return 'Name: ' + shape.dataItem.name;
      } else {
        return 'Name: ' + shape.dataItem.name + '\n' +
          'Address: ' + shape.dataItem.addressForGUI;
      }
    } else {
      return 'Name: ' + shape.dataItem.name + '\n' +
        'Type: ' + shape.dataItem.deviceType;
    }
  }

  getOpacity(dataItem): number {
    return this.isFiltered(dataItem) ? 1.0 : 0.3;
  }

  isFilteredForWidth(dataItem): boolean {
    return (dataItem.hasOwnProperty('filtered') && dataItem.filtered);
  }

  isFiltered(dataItem): boolean {
    return (dataItem.hasOwnProperty('filtered') && dataItem.filtered) || !dataItem.hasOwnProperty('filtered');
  }

  getBorderColor(dataItem, shapeType): string {
    if (_.isEqual(shapeType, TopologyElementType.node)) {
      return DiagramConstants.COLORS.darkBlue;
    } else {
      return DiagramConstants.COLORS.darkGrey;
    }
  }

  getShapeType(dataItem, shapeType): any {
    if (_.isEqual(shapeType, TopologyElementType.node)) {
      return dataItem.productType;
    } else {
      return dataItem.deviceType;
    }
  }

  getLabelText(dataItem): string {
    return dataItem.name;
  }

  convertHex(hex, opacity): string {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return 'rgba(' + r + ',' + g + ',' + b + ',' + opacity / 100 + ')';
  }
}
